import * as React from "react";
import { Checkbox, notification, Button, Input, Card, Popconfirm } from "antd";
import { Tooltip } from "../../components/Tooltip";
import { ServiceAccount } from "../../schema/types/serviceAccount/entity";
import { useAddKeyMutation, useDeleteKeysMutation } from "../../store/api/serviceAccountApi";
import { CreatedServiceAccountKeyModal } from "../../components/modals/keyModal";
import { CardTitle } from "../../components/antd/card/CardTitle";
import { CardContent } from "../../components/antd/card/CardContent";
import { linksOf } from "../../services/cpln";
import { useAppSelector } from "../../store/hooks";
import { selectOrg } from "../../store/contextSlice";
import { DateString } from "../../components/dateString";

interface Props {
  serviceAccount: ServiceAccount;
}
export const Keys: React.FC<Props> = ({ serviceAccount }) => {
  const org = useAppSelector(selectOrg);
  const selfLink = linksOf(serviceAccount).self!;

  const [addKeyMutation, { isLoading: isLoadingAdd, data: dataAdd, reset: resetAdd }] = useAddKeyMutation();
  const [deleteKeysMutation, { isLoading: isLoadingDelete }] = useDeleteKeysMutation();

  const [keyDescription, setKeyDescription] = React.useState("");
  const [keyPath, setKeyPath] = React.useState("");
  const [keysToDelete, setKeysToDelete] = React.useState<string[]>([]);

  async function handleAddKey() {
    try {
      await addKeyMutation({
        link: selfLink,
        description: keyDescription,
      }).unwrap();
      setKeyPath(`org-${org}_serviceaccount-${serviceAccount.name}_key-${keyDescription}`);
      notification.success({
        message: "Success",
        description: "Created a service account key",
      });
    } catch (e) {
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      notification.warning({
        message: "Failed",
        description: errorMessage,
      });
    }
  }

  function onToggleKeyToDelete(name: string, checked: boolean) {
    setKeysToDelete((keys) => {
      if (checked && !keys.includes(name)) {
        return [...keys, name];
      }
      if (!checked && keys.includes(name)) {
        return keys.filter((k) => k !== name);
      }

      // should not reach here
      return keys;
    });
  }

  async function handleDeleteKeys(): Promise<void> {
    try {
      await deleteKeysMutation({ link: selfLink, keys: keysToDelete }).unwrap();
      notification.success({
        message: "Success",
        description: "Deleted keys from service account",
      });
      setKeysToDelete([]);
    } catch (e) {
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      notification.warning({
        message: "Failed",
        description: errorMessage,
      });
      // TODO fix this globally
      if (e.response.status === 409) {
        // if (fetchItem) {
        //   await fetchItem();
        //   notification.info({
        //     message: "Updated Item",
        //     description: "Fetched the latest version of the item and discarded changes.",
        //   });
        // }
      }
    } finally {
    }
  }

  return (
    <div className="cpln-form-section cpln-ant-card-container">
      <Card>
        <CardTitle>Service Account Keys</CardTitle>
        <CardContent>
          <div style={{ maxWidth: 800 }}>
            <div>Add Key</div>
            <div className="mb-8 flex items-center gap-2">
              <Input
                value={keyDescription}
                onChange={(e) => setKeyDescription(e.target.value)}
                placeholder="Description"
              />
              <Button htmlType={`button`} onClick={handleAddKey} disabled={isLoadingAdd} loading={isLoadingAdd}>
                Add
              </Button>
              {dataAdd && dataAdd.length > 0 ? (
                <CreatedServiceAccountKeyModal
                  keyContent={dataAdd}
                  keyPath={keyPath}
                  onDone={() => {
                    setKeyDescription("");
                    setKeyPath("");
                    resetAdd();
                  }}
                  visible={dataAdd.length > 0}
                />
              ) : null}
            </div>
            <div>Keys</div>
            <div>
              <div className="flex items-center table-labels px-4 py-1">
                <Checkbox style={{ visibility: "hidden" }} />
                <div className="ml-2" style={{ width: 230 }}>Name</div>
                <div style={{ width: 300 }}>Description</div>
                <div>Created</div>
              </div>
              {(serviceAccount.keys || []).length === 0 ? (
                <div className="flex items-center border-b px-4 py-1">No Keys found</div>
              ) : null}
              {(serviceAccount.keys || []).map((key) => (
                <div className="flex items-center border-b px-4 py-1" key={key.name}>
                  <Checkbox
                    checked={keysToDelete.includes(key.name)}
                    onChange={(e) => onToggleKeyToDelete(key.name, e.target.checked)}
                  />
                  <div className="ml-2" style={{ width: 230 }}>
                    {key.name}
                  </div>
                  <Tooltip title={key.description} open={key.description.length >= 21}>
                    <div className="truncate" style={{ width: 300 }}>
                      {key.description}
                    </div>
                  </Tooltip>
                  <div>
                    <DateString iso={key.created as any} />
                  </div>
                </div>
              ))}
            </div>
            {(serviceAccount.keys || []).length > 0 ? (
              <Popconfirm
                title={`Are you sure?`}
                description={`This action cannot be undone`}
                onConfirm={handleDeleteKeys}
              >
                <Button
                  style={{ width: 215 }}
                  disabled={isLoadingAdd || isLoadingDelete || keysToDelete.length < 1}
                  loading={isLoadingDelete}
                  type={"primary"}
                  danger
                  className="mt-4"
                >
                  Delete Key{keysToDelete.length > 1 ? "s" : ""}
                </Button>
              </Popconfirm>
            ) : null}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
